// ========================================
// SophyPalavrasCruzadas - Página de Palavras Cruzadas
// ========================================
// Versão: 1.0
// Data: 01 de Novembro de 2025

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import PalavrasCruzadas from '../components/PalavrasCruzadas';

interface PalavraCruzada {
  numero: number;
  palavra: string;
  significado: string;
  direcao: 'horizontal' | 'vertical';
  inicio: { linha: number; coluna: number };
  tamanho: number;
}

interface Dica {
  numero: number;
  texto: string;
}

interface Grade {
  grade: (string | null)[][];
  palavras: PalavraCruzada[];
  dicas: {
    horizontais: Dica[];
    verticais: Dica[];
  };
  tema: string;
  dificuldade: number;
  tamanho: number;
  total_palavras: number;
}

interface TemasAreas {
  temas: string[];
  areas: string[];
}

export default function SophyPalavrasCruzadas() {
  const navigate = useNavigate();
  
  // Estados de configuração
  const [temasAreas, setTemasAreas] = useState<TemasAreas>({ temas: [], areas: [] });
  const [tema, setTema] = useState('');
  const [areaEstudo, setAreaEstudo] = useState('');
  const [dificuldade, setDificuldade] = useState(5);
  const [totalPalavras, setTotalPalavras] = useState(10);
  
  // Estados de jogo
  const [gradeAtual, setGradeAtual] = useState<Grade | null>(null);
  const [loading, setLoading] = useState(false);
  const [jogoIniciado, setJogoIniciado] = useState(false);
  const [timer, setTimer] = useState(0);
  const [verificado, setVerificado] = useState(false);
  const [acertos, setAcertos] = useState(0);
  const [erros, setErros] = useState(0);

  // Carregar temas e áreas disponíveis
  useEffect(() => {
    carregarTemasAreas();
  }, []);

  // Timer
  useEffect(() => {
    if (jogoIniciado && !verificado) {
      const interval = setInterval(() => {
        setTimer(prev => prev + 1);
      }, 1000);
      
      return () => clearInterval(interval);
    }
  }, [jogoIniciado, verificado]);

  const carregarTemasAreas = async () => {
    try {
      const response = await api.get('/sophygames/temas-areas');
      
      if (response.data.sucesso) {
        setTemasAreas(response.data.dados);
      }
    } catch (error: any) {
      console.error('[PALAVRAS-CRUZADAS] Erro ao carregar temas/áreas:', error);
    }
  };

  const gerarJogo = async () => {
    setLoading(true);
    try {
      console.log('[PALAVRAS-CRUZADAS] Gerando palavras cruzadas com configuração:', {
        tema: tema || 'Todos',
        dificuldade,
        totalPalavras
      });
      
      const response = await api.post('/sophygames/palavrascruzadas/gerar', {
        tema: tema || undefined,
        area_estudo: areaEstudo || undefined,
        dificuldade,
        totalPalavras
      });
      
      if (response.data.sucesso) {
        const grade = response.data.dados;
        setGradeAtual(grade);
        setJogoIniciado(true);
        setTimer(0);
        setVerificado(false);
        setAcertos(0);
        setErros(0);
        
        console.log('[PALAVRAS-CRUZADAS] Jogo gerado com sucesso:', {
          tema: grade.tema,
          tamanho: grade.tamanho,
          totalPalavras: grade.total_palavras,
          dicasHorizontais: grade.dicas.horizontais.length,
          dicasVerticais: grade.dicas.verticais.length
        });
      } else {
        alert('Erro ao gerar palavras cruzadas: ' + response.data.mensagem);
      }
    } catch (error: any) {
      console.error('[PALAVRAS-CRUZADAS] Erro ao gerar jogo:', error);
      alert('Erro ao gerar palavras cruzadas: ' + (error.response?.data?.mensagem || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleVerificarRespostas = (corretas: number, incorretas: number) => {
    setAcertos(corretas);
    setErros(incorretas);
    
    const totalCelulas = gradeAtual?.palavras.reduce((acc, p) => acc + p.tamanho, 0) || 0;
    const percentualAcerto = (corretas / totalCelulas) * 100;
    
    console.log('[PALAVRAS-CRUZADAS] Verificação:', {
      corretas,
      incorretas,
      totalCelulas,
      percentualAcerto: percentualAcerto.toFixed(1) + '%'
    });
    
    // Se 100% correto, finalizar jogo
    if (corretas === totalCelulas && incorretas === 0) {
      setTimeout(() => {
        finalizarJogo();
      }, 1000);
    }
  };

  const finalizarJogo = () => {
    setVerificado(true);
    
    const minutos = Math.floor(timer / 60);
    const segundos = timer % 60;
    
    const totalCelulas = gradeAtual?.palavras.reduce((acc, p) => acc + p.tamanho, 0) || 0;
    const percentualAcerto = ((acertos / totalCelulas) * 100).toFixed(1);
    
    const mensagem = `🎉 Parabéns! Você completou as palavras cruzadas!\n\n` +
      `⏱️ Tempo: ${minutos}m ${segundos}s\n` +
      `✅ Acertos: ${acertos}/${totalCelulas} (${percentualAcerto}%)\n` +
      `❌ Erros: ${erros}\n` +
      `🔤 Palavras: ${gradeAtual?.total_palavras}`;
    
    console.log('[PALAVRAS-CRUZADAS] Jogo concluído:', {
      tempo: `${minutos}m ${segundos}s`,
      acertos,
      erros,
      percentualAcerto
    });
    
    alert(mensagem);
  };

  const voltarConfig = () => {
    console.log('[PALAVRAS-CRUZADAS] Voltando para configuração...');
    setJogoIniciado(false);
    setGradeAtual(null);
    setTimer(0);
    setVerificado(false);
    setAcertos(0);
    setErros(0);
  };

  const formatarTempo = (segundos: number) => {
    const mins = Math.floor(segundos / 60);
    const secs = segundos % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="container-fluid">
      <div className="row mb-4">
        <div className="col-md-8">
          <h2 className="mb-2">
            <i className="fas fa-gamepad mr-2"></i>
            🧩 SophyGames - Palavras Cruzadas
          </h2>
          <p className="text-muted">
            Complete as palavras cruzadas com termos de Psicologia
          </p>
        </div>
        <div className="col-md-4 text-right">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => navigate('/sophygames')}
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Voltar ao SophyGames
          </button>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          {!jogoIniciado ? (
            // ========================================
            // Configuração do Jogo
            // ========================================
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">⚙️ Configure suas Palavras Cruzadas</h3>
              </div>
              <div className="card-body">
                <div className="row">
                  {/* Filtros */}
                  <div className="col-md-6 mb-3">
                    <label htmlFor="palavrasCruzadasTema">📚 Tema (opcional)</label>
                    <select
                      className="form-control"
                      id="palavrasCruzadasTema"
                      value={tema}
                      onChange={(e) => setTema(e.target.value)}
                    >
                      <option value="">Todos os temas</option>
                      {temasAreas.temas.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-6 mb-3">
                    <label htmlFor="palavrasCruzadasAreaEstudo">🎯 Área de Estudo (opcional)</label>
                    <select
                      className="form-control"
                      id="palavrasCruzadasAreaEstudo"
                      value={areaEstudo}
                      onChange={(e) => setAreaEstudo(e.target.value)}
                    >
                      <option value="">Todas as áreas</option>
                      {temasAreas.areas.map((a) => (
                        <option key={a} value={a}>{a}</option>
                      ))}
                    </select>
                  </div>

                  {/* Dificuldade */}
                  <div className="col-md-6 mb-3">
                    <label htmlFor="palavrasCruzadasDificuldade">⭐ Dificuldade Máxima: {dificuldade}/10</label>
                    <input
                      type="range"
                      className="custom-range"
                      id="palavrasCruzadasDificuldade"
                      min="1"
                      max="10"
                      value={dificuldade}
                      onChange={(e) => setDificuldade(parseInt(e.target.value))}
                    />
                    <small className="form-text text-muted">
                      Termos com dificuldade de 1 até {dificuldade}
                    </small>
                  </div>

                  {/* Total de Palavras */}
                  <div className="col-md-6 mb-3">
                    <label>🔢 Quantidade de Palavras: {totalPalavras}</label>
                    <input
                      type="range"
                      className="custom-range"
                      min="5"
                      max="20"
                      value={totalPalavras}
                      onChange={(e) => setTotalPalavras(parseInt(e.target.value))}
                    />
                    <small className="form-text text-muted">
                      A grade será gerada dinamicamente conforme o cruzamento das palavras
                    </small>
                  </div>
                </div>

                {/* Botão Gerar */}
                <div className="text-center mt-4">
                  <button
                    className="btn btn-primary btn-lg"
                    onClick={gerarJogo}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm mr-2"></span>
                        Gerando...
                      </>
                    ) : (
                      <>🎮 Gerar Palavras Cruzadas</>
                    )}
                  </button>
                </div>

                {/* Informação */}
                <div className="alert alert-info mt-4 mb-0">
                  <strong>ℹ️ Como funciona:</strong> Cada grade de palavras cruzadas é gerada 
                  dinamicamente com cruzamento inteligente de palavras a partir dos {' '}
                  <strong>40.000+ termos</strong> de Psicologia cadastrados. Configure os 
                  filtros acima para personalizar seu jogo!
                </div>
              </div>
            </div>
          ) : (
            // ========================================
            // Jogo Iniciado
            // ========================================
            <>
              {/* Timer e Progresso */}
              <div className="alert alert-info d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                <div>
                  <strong>⏱️ Tempo: {formatarTempo(timer)}</strong>
                </div>
                {verificado && (
                  <div>
                    <strong>
                      ✅ Acertos: {acertos} | ❌ Erros: {erros}
                    </strong>
                  </div>
                )}
                <div>
                  <button className="btn btn-sm btn-danger" onClick={voltarConfig}>
                    🔄 Nova Configuração
                  </button>
                </div>
              </div>

              {/* Componente Palavras Cruzadas */}
              {gradeAtual && (
                <PalavrasCruzadas
                  grade={gradeAtual.grade}
                  palavras={gradeAtual.palavras}
                  dicas={gradeAtual.dicas}
                  tema={gradeAtual.tema}
                  dificuldade={gradeAtual.dificuldade}
                  onVerificarRespostas={handleVerificarRespostas}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
